import { Injectable, computed, signal } from '@angular/core';
import type {
  CardReferenceModel,
  LastTransactionModel,
  MemberProfileModel,
} from '../api/auth.models';

export interface AuthenticatedSession {
  cardNumber: string;
  mobileNumber: string;
  member: MemberProfileModel;
  virtualCard: CardReferenceModel;
  physicalCard: CardReferenceModel | null;
  lastTransaction: LastTransactionModel | null;
}

@Injectable({ providedIn: 'root' })
export class MemberSessionService {
  private readonly storageKey = 'auth.activeSession';
  private readonly sessionSignal = signal<AuthenticatedSession | null>(this.loadFromStorage());
  readonly session = computed(() => this.sessionSignal());

  setSession(session: AuthenticatedSession): void {
    this.sessionSignal.set(session);
    localStorage.setItem(this.storageKey, JSON.stringify(session));
  }

  getSession(): AuthenticatedSession | null {
    return this.sessionSignal();
  }

  clearSession(): void {
    this.sessionSignal.set(null);
    localStorage.removeItem(this.storageKey);
    sessionStorage.removeItem(this.storageKey);
  }

  private loadFromStorage(): AuthenticatedSession | null {
    const raw = localStorage.getItem(this.storageKey) ?? sessionStorage.getItem(this.storageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as AuthenticatedSession;
      if (!parsed.cardNumber || !parsed.member || !parsed.virtualCard?.cardNumber) {
        return null;
      }

      // Migrate any pre-existing sessionStorage value to localStorage.
      if (!localStorage.getItem(this.storageKey)) {
        localStorage.setItem(this.storageKey, raw);
      }
      sessionStorage.removeItem(this.storageKey);

      return parsed;
    } catch {
      return null;
    }
  }
}
